import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ChatItem } from '~/components/chat/ChatItem'
import { LoadingState } from '~/components/common/LoadingSpinner'
import { TaskItem as TaskItemComponent } from '~/components/task/TaskItem'
import { useConversationsContext } from '~/contexts/ConversationContext'
import { useConversationUuid } from '~/hooks'
import { AgentTaskStatus, agentTaskStatusFromJSON, commandTypeFromJSON } from '~/protos/agents'
import { Conversation } from '~/types/Conversations'
import { getEffectiveTaskStatus } from '~/task/commandTypeUtils'
import { sortTasksForDisplay } from '~/utils/taskSort'
import { generateTaskTitle, getConversationDisplayTitle } from '~/utils/taskTitles'
import { TabType } from './Sidebar'

// Task data structure for cleaner separation
export interface TaskData {
  taskId: number
  title: string
  status: AgentTaskStatus
  createdTime: string
  conversationUuid: string
  conversation: Conversation
  cmdType?: string | number
  isCancelRequested?: boolean
  scheduledTaskId?: number | null
}

// Wrapper component for TaskItem to handle hooks properly
export interface TaskItemWrapperProps {
  task: TaskData
  noHover?: boolean
}

export function TaskItemWrapper({ task, noHover = false }: TaskItemWrapperProps) {
  const navigate = useNavigate()
  const { workspaceUuid } = useParams<{ workspaceUuid: string }>()
  const [searchParams] = useSearchParams()

  const isSelected = task.taskId === Number(searchParams.get('task_id'))
  const taskStatus = agentTaskStatusFromJSON(task.status)

  const commandType = commandTypeFromJSON(
    task.cmdType ?? task.conversation?.conversation_metadata?.command
  )
  const displayStatus = getEffectiveTaskStatus(taskStatus, commandType)

  return (
    <TaskItemComponent
      taskId={task.taskId}
      title={task.title}
      taskStatus={displayStatus}
      conversationUuid={task.conversationUuid}
      isSelected={isSelected}
      onClick={() =>
        navigate(`/w/${workspaceUuid}/c/${task.conversationUuid}?task_id=${task.taskId}`)
      }
      noHover={noHover}
      cmdType={task.cmdType}
      isCancelRequested={task.isCancelRequested}
      scheduledTaskId={task.scheduledTaskId}
    />
  )
}

interface Props {
  view: TabType
}

export function Conversations({ view }: Props) {
  const { conversations, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useConversationsContext()
  const currentUuid = useConversationUuid()
  const { workspaceUuid } = useParams<{ workspaceUuid: string }>()
  const navigate = useNavigate()
  const lastFetchTimeRef = useRef<number>(0)

  // Delayed loading state - only show loading indicator after 1 second
  const [showLoading, setShowLoading] = useState(false)

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null

    if (isFetching && conversations.length === 0) {
      timer = setTimeout(() => {
        setShowLoading(true)
      }, 500)
    } else {
      setShowLoading(false)
    }

    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [isFetching, conversations.length])

  // All hooks must be called before any conditional returns
  const { ref, inView } = useInView({
    threshold: 0.1, // Trigger when 10% visible (closer to the end)
    rootMargin: '100px' // Start loading 100px before reaching the element
  })

  // Debounced fetch function to prevent excessive calls
  const debouncedFetchNextPage = useCallback(async () => {
    const now = Date.now()
    // Prevent fetching more than once every 500ms
    if (now - lastFetchTimeRef.current < 500) {
      return
    }

    if (hasNextPage && !isFetchingNextPage) {
      lastFetchTimeRef.current = now
      try {
        await fetchNextPage()
      } catch (error) {
        console.error('Error fetching next page:', error)
      }
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    // Only trigger pagination if we have conversations and we're actually at the bottom
    // This prevents initial fetch conflicts when the component first mounts
    if (inView && conversations.length > 0) {
      debouncedFetchNextPage()
    }
  }, [inView, debouncedFetchNextPage, conversations])

  if (view === 'chats') {
    // Chats are already filtered server-side via type=chats.
    // Keep frontend behavior mode-agnostic to avoid drift when command lists evolve.
    const filteredChats = conversations

    // Show loading spinner for initial load (only after 1 second delay)
    if (showLoading && filteredChats.length === 0) {
      return <LoadingState message='Loading chats...' className='py-12' />
    }

    return (
      <div className='flex flex-col'>
        <AnimatePresence initial={false}>
          {filteredChats.map(conversation => {
            const isSelected = currentUuid === conversation.uuid
            return (
              <motion.div
                key={conversation.uuid}
                layout
                initial={false}
                transition={{ type: 'spring', stiffness: 500, damping: 40, mass: 0.8 }}
              >
                <ChatItem
                  uuid={conversation.uuid}
                  title={getConversationDisplayTitle(
                    conversation.title,
                    conversation.conversation_metadata?.mode
                  )}
                  isSelected={isSelected}
                  onClick={() => navigate(`/w/${workspaceUuid}/c/${conversation.uuid}`)}
                />
              </motion.div>
            )
          })}
        </AnimatePresence>
        <div ref={ref} className='py-4 text-center text-on-background dark:text-light10 text-sm'>
          {isFetchingNextPage && 'Loading more conversations...'}
        </div>
      </div>
    )
  }

  // Task view logic
  const tasks = conversations
    .flatMap(conversation =>
      sortTasksForDisplay(conversation.tasks || []).map(task => ({
        taskId: task.task_id,
        title: generateTaskTitle(commandTypeFromJSON(task.cmd_type)),
        status: task.status as unknown as AgentTaskStatus,
        createdTime: task.created_ts,
        conversationUuid: conversation.uuid,
        conversation: conversation,
        cmdType: task.cmd_type,
        isCancelRequested: task.is_cancel_requested,
        scheduledTaskId: task.scheduled_task_id
      }))
    )
    .sort((a, b) => b.taskId - a.taskId)

  // Show loading spinner for initial load (only after 1 second delay)
  if (showLoading && tasks.length === 0) {
    return <LoadingState message='Loading tasks...' className='py-12' />
  }

  return (
    <div className='flex flex-col gap-y-2'>
      {tasks.map((task, index) => (
        <div key={`task-${task.taskId}-${index}`}>
          {index > 0 && <div className='h-px bg-tokens-neutral-fill-fill-2 my-2' />}
          <TaskItemWrapper task={task} />
        </div>
      ))}
      <div ref={ref} className='py-4 text-center text-on-background dark:text-light10 text-sm'>
        {isFetchingNextPage && 'Loading more tasks...'}
      </div>
    </div>
  )
}
